/**
 * The closed vocabularies a composite-source definition is written in.
 *
 * These are WIRE WORDS. They are stored inside `definitionJson` and re-read by the M1 compiler,
 * so they are copied verbatim from the server's own vocabulary
 * (aiva.api `MODS.Forge.Models/Models/DataSources/DataSourceVocabularies.cs`) instead of being
 * invented here. Renaming one on this side does not rename it in the definitions already stored.
 *
 * Wave A's `config/compositeSourceEnums.js` owns the WORKSPACE vocabulary — status, target kind,
 * copy. This file owns the DEFINITION vocabulary. They stay apart because the workspace list
 * never reads a strategy and the definition model never reads a status; the one word both need
 * (the scope kind) is imported from there rather than restated here.
 */

/** The legacy definition format. New documents remain v1 until v2 behavior is explicitly authored. */
export const DefinitionVersion = 1;

/** The format that carries spatial-interest roles and the registerRows tag bridge. */
export const SpatialDefinitionVersion = 2;

/** The format that carries exact traversal identity and plural spatial bindings. */
export const SpatialBindingsDefinitionVersion = 3;

/** Formats this wizard may load without dropping authored behavior. */
export const SupportedDefinitionVersions = Object.freeze([
  DefinitionVersion,
  SpatialDefinitionVersion,
  SpatialBindingsDefinitionVersion,
]);

export const SpatialNoTargetPolicies = Object.freeze({
  PromoteVisibleAnchors: 'promoteVisibleAnchors',
  RetainVisibleAnchorsAsContext: 'retainVisibleAnchorsAsContext',
});

/** Compatibility default for v3 definitions authored before the choice was exposed. */
export const SpatialNoTargetPolicy = SpatialNoTargetPolicies.PromoteVisibleAnchors;

export function normalizeSpatialNoTargetPolicy(value) {
  return value === SpatialNoTargetPolicies.RetainVisibleAnchorsAsContext
    ? SpatialNoTargetPolicies.RetainVisibleAnchorsAsContext
    : SpatialNoTargetPolicy;
}

export function spatialNoTargetPolicyForFallback(enabled) {
  return enabled === false
    ? SpatialNoTargetPolicies.RetainVisibleAnchorsAsContext
    : SpatialNoTargetPolicies.PromoteVisibleAnchors;
}

/** Compatibility default for typed focus authored before owning context was configurable. */
export function normalizeSpatialIncludeOwningContext(value) {
  return value !== false;
}

export const SpatialTagTypeOptions = Object.freeze([
  { value: 'baseTag', text: 'Base Tag' },
  { value: 'equipmentTag', text: 'Equipment' },
  { value: 'pipelineTag', text: 'Pipeline' },
  { value: 'structuralTag', text: 'Structural' },
  { value: 'anomalyTag', text: 'Anomaly' },
  { value: 'inspectionTag', text: 'Inspection' },
  { value: 'annotationTag', text: 'Annotation' },
  { value: 'avaInspectTag', text: 'AVA Inspect' },
  { value: 'scaffoldTag', text: 'Scaffold' },
  { value: 'permitTag', text: 'Permit' },
  { value: 'containerTag', text: 'Container' },
]);

/** The root's node id. Reserved: no edge may claim it (DataSourceDefinitionValidator.cs:389-396). */
export const RootNodeId = 'root';

/**
 * Node kinds a definition may reference (`DataSourceNodeKinds`). The server's authoring graph
 * decides which kinds are reachable from a selected root; this vocabulary only preserves their
 * wire words and labels. `taskBoard` is the DS-11 root kind: a facility-owned task board whose
 * rows are its tasks. `finding` (an AI finding, `AnomalyInsights`) and `scanImage` (its 360 image)
 * are the AVA-560 composite-roots nodes; `tag` and `finding` are also root kinds.
 */
export const CompositeNodeKind = Object.freeze({
  Register: 'register',
  TaskBoard: 'taskBoard',
  RegisterRows: 'registerRows',
  Tag: 'tag',
  MasterTag: 'masterTag',
  Cml: 'cml',
  Tml: 'tml',
  Document: 'document',
  Finding: 'finding',
  ScanImage: 'scanImage',
});

/**
 * Root kinds that name no id (AVA-560). A `finding` root is every AI finding of the definition's
 * facility, so there is nothing for an id to name, and the api refuses one when sent
 * (`RootIdNotApplicable`, aiva.api `DataSourceFindingRootRules`). The wire root is
 * `{kind, scopeKind}`.
 */
export const RootKindsWithoutRootId = Object.freeze([CompositeNodeKind.Finding]);

/**
 * Root kinds whose `rootId` is a TAG KIND rather than an entity id (AVA-560, decision D1: one
 * required kind per root). A `tag` root reads every tag of one kind, named by its stored
 * discriminator: `{kind: 'tag', rootId: 'ForgeAvaInspect', scopeKind}` (aiva.api
 * `DataSourceTagRootRules`: "The tag root's identity is the TAG KIND in root.rootId").
 */
export const RootKindsIdentifiedByTagKind = Object.freeze([CompositeNodeKind.Tag]);

/**
 * The tag kinds a `tag` root may read, as the server's TPH discriminators
 * (`DataSourceSchemaGraph.TagDiscriminators`, minus the abstract `ForgeBaseTag`) with the names
 * the product shows for them. The values are wire words; the texts are labels.
 */
export const TagRootDiscriminatorOptions = Object.freeze([
  Object.freeze({ value: 'ForgeAvaInspect', text: 'AVA Inspect' }),
  Object.freeze({ value: 'ForgeEquipment', text: 'Equipment' }),
  Object.freeze({ value: 'ForgePipeline', text: 'Pipeline' }),
  Object.freeze({ value: 'ForgeStructural', text: 'Structural' }),
  Object.freeze({ value: 'ForgeAnomaly', text: 'Anomaly' }),
  Object.freeze({ value: 'ForgeInspection', text: 'Inspection' }),
  Object.freeze({ value: 'ForgeScaffold', text: 'Scaffold' }),
  Object.freeze({ value: 'ForgeAnnotation', text: 'Annotation' }),
]);

/** The product's name for a tag discriminator, or the wire word when it is not a root option. */
export function tagDiscriminatorLabel(discriminator) {
  const option = TagRootDiscriminatorOptions.find((candidate) => candidate.value === discriminator);
  return option ? option.text : (discriminator || '');
}

/** True when a root of this kind is identified without a `rootId`. */
export function isRootWithoutRootId(kind) {
  return RootKindsWithoutRootId.indexOf(kind) !== -1;
}

/** True when a root of this kind names a tag kind in its `rootId`. */
export function isRootIdentifiedByTagKind(kind) {
  return RootKindsIdentifiedByTagKind.indexOf(kind) !== -1;
}

/** Node kinds that carry a tag id, and so may be designated as the TagId source. */
export const TagFamilyNodeKinds = Object.freeze([CompositeNodeKind.Tag, CompositeNodeKind.MasterTag]);

/** Exact tag discriminators whose spatial subject kind is deterministic. */
export const SpatialTargetTagDiscriminators = Object.freeze(['ForgeAnomaly', 'ForgeInspection']);

/**
 * How an edge folds its target in (`DataSourceStrategies`). Note `fanOut`, not `fan-out`:
 * the server compares ordinally.
 */
export const CompositeStrategy = Object.freeze({
  Single: 'single',
  Latest: 'latest',
  Count: 'count',
  Exists: 'exists',
  FanOut: 'fanOut',
  List: 'list',
});

/**
 * The scalar strategies. A scalar edge IS one column: it projects no others, nothing can be
 * joined onto it, and it must name itself with an edge alias.
 */
export const ScalarStrategies = Object.freeze([
  CompositeStrategy.Count,
  CompositeStrategy.Exists,
  CompositeStrategy.List,
]);

/** The column `list` aggregates by default. Mirrors DataSourceStrategies.DefaultListColumn. */
export const DefaultListColumn = 'Name';

/** What one row of the compiled view means (`DataSourceGrainKinds`). */
export const CompositeGrain = Object.freeze({
  Root: 'root',
  FanOut: 'fanOut',
});

/**
 * Aliases the compiler emits or the engine claims (`DataSourceReservedColumns`). An authored
 * alias may take none of them — `ForgeAssetId` most of all, because the engine resolves a view's
 * facility scope column by looking for it FIRST, so an authored column wearing that name would
 * scope the whole view by a user-controlled value.
 */
export const ReservedColumnAliases = Object.freeze([
  'Id',
  'FacilityId',
  'TagId',
  'TagIds',
  'ForgeAssetId',
  '__rn',
]);

/** Longest identifier the server's allow-list accepts. */
export const SafeSqlNameMaxLength = 128;

const safeSqlNamePattern = /^[a-zA-Z_][a-zA-Z0-9_]{0,127}$/;

/** True when the strategy yields one scalar column instead of a joinable node. */
export function isScalarStrategy(kind) {
  return !!kind && ScalarStrategies.indexOf(kind) !== -1;
}

/** True when a planned node may be authored as the graph-native highlight target. */
export function isSpatialTargetNode(node) {
  if (!node || node.isScalar) return false;
  if (node.type === CompositeNodeKind.Cml || node.type === CompositeNodeKind.Tml) return true;
  return node.type === CompositeNodeKind.Tag
    && SpatialTargetTagDiscriminators.indexOf(node.discriminator) !== -1;
}

/** True when a planned node may be authored as gray component context. */
export function isSpatialContextNode(node) {
  return !!node && !node.isScalar && node.type === CompositeNodeKind.MasterTag;
}

/** True for the dedicated v2 Task-to-RegisterRows fan-out tag designation. */
export function isRegisterRowsTagSourceNode(node) {
  return !!node
    && !node.isScalar
    && node.type === CompositeNodeKind.RegisterRows
    && node.strategy === CompositeStrategy.FanOut;
}

/**
 * True when a planned node may be designated as the TagId source: a tag-family node or the v2
 * registerRows fan-out bridge, and never the root. Before AVA-560 no root was tag-family; a `tag`
 * root now is, but the api refuses its own rows as the TagId source
 * (`TagRootSpatialNotSupported`: "A tag root's own rows cannot drive 3D yet"), so the wizard
 * offers a joined tag node instead.
 */
export function isTagSourceNode(node) {
  if (!node || node.id === RootNodeId) return false;
  return TagFamilyNodeKinds.indexOf(node.type) !== -1 || isRegisterRowsTagSourceNode(node);
}

/**
 * True when the alias collides with a compiler-emitted column. Case-INSENSITIVE, because SQL
 * Server's default collation is: `tagid` and `TagId` are one column.
 */
export function isReservedAlias(alias) {
  if (!alias) return false;
  const lowered = String(alias).toLowerCase();
  return ReservedColumnAliases.some((reserved) => reserved.toLowerCase() === lowered);
}

/**
 * True when the name survives bracketing into SQL. Mirrors `DataSourceSqlText.IsSafeName`
 * (`^[a-zA-Z_][a-zA-Z0-9_]{0,127}$`) so the wizard cannot author a name the compiler will
 * refuse — note it excludes spaces, which is why aliases read `PipelineTagName` and never
 * `Pipeline Tag Name`.
 */
export function isSafeSqlName(name) {
  return !!name && safeSqlNamePattern.test(name);
}

/**
 * Kind labels the bare first-character transform gets wrong. `taskBoard` must read as the
 * product's own term — the Tasks feature calls these "Task boards" — where the transform would
 * emit the non-word "TaskBoard". `masterTag` is deliberately NOT here: "MasterTag" is the term
 * the rest of the product uses, and splitting it would invent a different one.
 */
export const NodeKindLabelOverrides = Object.freeze({
  [CompositeNodeKind.TaskBoard]: 'Task board',
  [CompositeNodeKind.Cml]: 'CML',
  [CompositeNodeKind.Tml]: 'TML',
  [CompositeNodeKind.Finding]: 'AI findings',
  [CompositeNodeKind.ScanImage]: '360 image',
});

/**
 * A wire kind rendered for a human — the ONE kind→label authority: the source step's chip, the
 * tree, and the diagram all take a kind's wording from here, so what a curator reads for a kind
 * is decided in exactly one place. The vocabulary's words are camelCase because the compiler
 * reads them, but they reach those surfaces as labels whenever nothing better is known — and a
 * label starting lowercase breaks the UI's first-character rule.
 *
 * Outside the overrides, only the first character is touched.
 */
export function humanizeNodeKind(kind) {
  if (!kind) return '';
  return NodeKindLabelOverrides[kind]
    || `${String(kind).charAt(0).toUpperCase()}${String(kind).slice(1)}`;
}

/**
 * Rewrites arbitrary text into a name `isSafeSqlName` accepts, for the aliases the model mints
 * on the author's behalf. Anything outside the allow-list becomes `_`; a leading digit gains a
 * `_` prefix rather than being dropped, so `360Scan` stays distinguishable from `Scan`.
 */
export function toSafeSqlName(text, fallback) {
  const source = text === null || text === undefined ? '' : String(text);
  let candidate = source.replace(/[^a-zA-Z0-9_]/g, '_');

  if (/^[0-9]/.test(candidate)) candidate = `_${candidate}`;
  candidate = candidate.slice(0, SafeSqlNameMaxLength);

  return isSafeSqlName(candidate) ? candidate : fallback;
}
